// studio/IntentPanel.jsx — Panneau conversation : Intention → Plan → Fichiers → Aperçu du résultat.
import { useState } from "react";
import { ST, PNL, btnP, btnG, tabS, chip, STATUS_META } from "@/internal/studio/theme";
import { AGENTS } from "@/internal/studio/simEngine";
import { Sparkles, Zap, PencilLine, MoreHorizontal, FileCode2, CheckCircle2, Bot, History } from "lucide-react";

const TABS = ["Intention", "Plan", "Fichiers", "Agents", "Historique"];
const badge = (a) => (a === "créer" ? { l: "Nouveau", c: ST.green } : a === "modifier" ? { l: "Modification", c: ST.gold } : { l: "Mise à jour", c: ST.blue });

export default function IntentPanel({ build, builds, phase, progress, busy, onSubmitIntent, onGenerateCode, onOpenBuild }) {
  const [tab, setTab] = useState("Intention");
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const plan = build?.plan;
  const canGen = phase === "plan";
  const stepDone = (i) => (phase !== "plan" ? true : true);

  const intentInput = (
    <div style={{ display: "flex", gap: 8, padding: "10px 0" }}>
      <input data-testid="studio-intent-input" value={draft} onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && draft.trim()) { onSubmitIntent(draft); setDraft(""); setEditing(false); } }}
        placeholder='Décrivez votre intention… Ex : "Ajoute une carte Chauffeur privé sur AYA Transport."'
        style={{ flex: 1, background: ST.soft, border: `1px solid ${ST.border}`, borderRadius: 10, padding: "10px 13px", color: ST.text, fontSize: 12.5, outline: "none", fontFamily: "inherit" }} />
      <button data-testid="studio-intent-send" onClick={() => { if (draft.trim()) { onSubmitIntent(draft); setDraft(""); setEditing(false); } }} disabled={busy || !draft.trim()} style={{ ...btnP, opacity: busy || !draft.trim() ? 0.5 : 1 }}>
        {busy ? "AYA analyse…" : "Envoyer"}
      </button>
    </div>
  );

  return (
    <section style={PNL} data-testid="studio-intent-panel">
      <div style={{ display: "flex", borderBottom: `1px solid ${ST.border}`, padding: "0 6px", flexShrink: 0 }}>
        {TABS.map((t) => <button key={t} data-testid={`intent-tab-${t.toLowerCase()}`} onClick={() => setTab(t)} style={tabS(tab === t)}>{t}</button>)}
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
        {tab === "Intention" && (
          <>
            {!build && !busy && <div style={{ color: ST.muted, fontSize: 12.5, marginBottom: 8 }}>Aucune intention en cours. Décrivez ce que vous voulez construire — AYA analyse l'existant et propose un plan.</div>}
            {build && (
              <>
                <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#334155,#64748B)", flexShrink: 0, display: "grid", placeItems: "center", fontSize: 12, fontWeight: 900, color: "#fff" }}>C</div>
                  <div data-testid="intent-user-bubble" style={{ background: "rgba(56,189,248,0.08)", border: "1px solid rgba(56,189,248,0.25)", borderRadius: "4px 12px 12px 12px", padding: "10px 13px", fontSize: 12.5, color: ST.text, lineHeight: 1.5 }}>{build.intent}</div>
                </div>
                <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#1DB954,#0E7A38)", flexShrink: 0, display: "grid", placeItems: "center" }}><Sparkles size={14} color="#fff" /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: ST.text, marginBottom: 6 }}>AYA <span style={{ color: ST.muted, fontWeight: 600 }}>· Intention comprise. Voici le plan proposé :</span></div>
                    <div data-testid="intent-plan-steps" style={{ background: ST.soft, border: `1px solid ${ST.border}`, borderRadius: 12, padding: "10px 12px" }}>
                      {(plan?.steps || []).map((s, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12, color: ST.text, animation: `studioFade 0.4s ease ${i * 0.12}s both` }}>
                          <CheckCircle2 size={14} color={stepDone(i) ? ST.green : ST.muted} /> {s}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 900, color: ST.muted, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><FileCode2 size={12} /> FICHIERS À MODIFIER ({plan?.files?.length || 0})</div>
                  {(plan?.files || []).map((f, i) => {
                    const b = badge(f.action);
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: ST.soft, border: `1px solid ${ST.border}`, borderRadius: 9, padding: "7px 11px", marginBottom: 5 }}>
                        <FileCode2 size={13} color={ST.blue} />
                        <span style={{ flex: 1, fontSize: 11, fontFamily: ST.mono, color: ST.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.path}</span>
                        <span style={chip(b.c)}>{b.l}</span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 900, color: ST.muted, marginBottom: 6 }}>💡 APERÇU DU RÉSULTAT</div>
                  <div style={{ background: "#F4F6F4", borderRadius: 12, padding: 12, fontSize: 12, color: "#1A2420" }} data-testid="intent-result-preview">
                    <div style={{ fontWeight: 900, marginBottom: 4 }}>{plan?.title}</div>
                    <div style={{ fontSize: 11.5, color: "#51605A", lineHeight: 1.5 }}>{plan?.summary}</div>
                    <div style={{ marginTop: 6 }}><span style={chip(plan?.risk === "critical" || plan?.risk === "high" ? ST.red : plan?.risk === "medium" ? ST.amber : ST.green2)}>risque {plan?.risk}</span></div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button data-testid="studio-generate-code" onClick={onGenerateCode} disabled={!canGen} style={{ ...btnP, opacity: canGen ? 1 : 0.45 }}>
                    <Zap size={13} /> {phase === "coding" ? `Génération… ${progress}%` : phase === "plan" ? "Générer le code" : "Code généré ✓"}
                  </button>
                  <button data-testid="studio-edit-request" onClick={() => setEditing(true)} style={btnG}><PencilLine size={13} /> Modifier la demande</button>
                  <button style={btnG}><MoreHorizontal size={13} /> Autres options</button>
                </div>
              </>
            )}
            {(!build || editing) && intentInput}
          </>
        )}
        {tab === "Plan" && (plan?.steps || []).map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 8, padding: "6px 0", fontSize: 12.5, color: ST.text }}><CheckCircle2 size={14} color={ST.green} /> {i + 1}. {s}</div>
        ))}
        {tab === "Fichiers" && (plan?.files || []).map((f, i) => (
          <div key={i} style={{ padding: "7px 0", borderBottom: `1px solid ${ST.border2}` }}>
            <div style={{ fontSize: 11.5, fontFamily: ST.mono, color: ST.text }}>{f.path} <span style={chip(badge(f.action).c)}>{badge(f.action).l}</span></div>
            <div style={{ fontSize: 11, color: ST.muted, marginTop: 2 }}>{f.description}</div>
          </div>
        ))}
        {tab === "Agents" && AGENTS.map((a) => (
          <div key={a.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${ST.border2}` }}>
            <Bot size={15} color={ST.green} />
            <div style={{ flex: 1 }}><div style={{ fontSize: 12.5, fontWeight: 800, color: ST.text }}>{a.name}</div><div style={{ fontSize: 11, color: ST.muted }}>{a.task}</div></div>
          </div>
        ))}
        {tab === "Historique" && (
          <>
            {(builds || []).length === 0 && <div style={{ color: ST.muted, fontSize: 12 }}>Aucun build.</div>}
            {(builds || []).map((b) => {
              const m = STATUS_META[b.status] || STATUS_META.plan;
              return (
                <button key={b.build_id} data-testid={`history-build-${b.build_id}`} onClick={() => onOpenBuild(b.build_id)}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: build?.build_id === b.build_id ? "rgba(46,226,123,0.07)" : "transparent", border: `1px solid ${ST.border2}`, borderRadius: 10, padding: "8px 11px", marginBottom: 6, cursor: "pointer" }}>
                  <History size={13} color={ST.muted} />
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: ST.text }}>{b.plan?.title}</span>
                  <span style={chip(m.c)}>{m.l}</span>
                </button>
              );
            })}
          </>
        )}
      </div>
    </section>
  );
}
